import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Trophy, Coins, Star, Swords, Users, Skull } from "lucide-react";

const TABS = [
  { id: "level",  labelKey: "ranks.level",  Icon: Star,   color: "#E5C34A" },
  { id: "gold",   labelKey: "ranks.gold",   Icon: Coins,  color: "#B89C3A" },
  { id: "arena",  labelKey: "ranks.arena",  Icon: Swords, color: "#8A0303" },
  { id: "boss",   labelKey: "ranks.boss",   Icon: Skull,  color: "#E33333" },
  { id: "guilds", labelKey: "ranks.guilds", Icon: Users,  color: "#4A7BC7" },
];

export default function RanksView({ ch }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("level");

  useEffect(() => { api.leaderboards().then(setData); }, []);

  if (!data) return <div className="panel p-4 text-center text-[#666] text-sm italic">{t("ranks.loading")}</div>;

  return (
    <div className="space-y-2">
      <div className="panel p-2 flex gap-1 flex-wrap" data-testid="ranks-subnav">
        {TABS.map(tt => {
          const Icon = tt.Icon;
          const active = tab === tt.id;
          return (
            <button
              key={tt.id}
              data-testid={`rank-tab-${tt.id}`}
              onClick={() => setTab(tt.id)}
              className={`flex-1 py-2 px-2 border font-heading text-[10px] tracking-widest uppercase flex items-center justify-center gap-1 ${
                active ? "border-[#E55A00] text-[#E55A00]" : "border-[#262626] text-[#8c8c8c]"
              }`}
            >
              <Icon className="w-3 h-3" strokeWidth={1.5} style={{ color: active ? "#E55A00" : tt.color }} />
              {t(tt.labelKey)}
            </button>
          );
        })}
      </div>

      <div className="panel p-3" data-testid={`ranks-${tab}`}>
        {tab === "level" && <RankList rows={data.top_level.map(r => ({ name: r.name, value: `Lv ${r.level}` }))} />}
        {tab === "gold" && <RankList rows={data.top_gold.map(r => ({ name: r.name, value: `${r.gold}g` }))} />}
        {tab === "arena" && <RankList rows={(data.top_arena || []).map(r => ({ name: r.name, value: `${r.arena_wins} ${t("ranks.wins")}` }))} empty={t("ranks.noArena")} />}
        {tab === "boss" && (
          <>
            <div className="text-[10px] text-[#8c8c8c] uppercase tracking-widest font-heading mb-2">
              {data.boss_name || t("ranks.boss")}
            </div>
            <RankList rows={data.top_boss.map(r => ({ name: r.name, value: `${r.damage} ${t("ranks.damage")}` }))} empty={t("ranks.noBoss")} />
          </>
        )}
        {tab === "guilds" && (
          <RankList
            rows={data.top_guilds.map(g => ({ name: `«${g.name}» [${g.tag}]`, value: `Lv ${g.level} · ${g.member_count} mbrs` }))}
            empty={t("ranks.noGuilds")}
          />
        )}
      </div>
    </div>
  );
}

function RankList({ rows, empty }) {
  const { t } = useTranslation();
  if (!rows || rows.length === 0)
    return <p className="italic text-[#666] text-xs">{empty || t("ranks.none")}</p>;
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={i} className="flex justify-between items-center border-b border-[#1a1a1a] py-1 text-xs">
          <span className="text-[#E2DFD8]">
            <span className="font-heading text-[#B89C3A] w-6 inline-block">
              {i === 0 ? "I" : i === 1 ? "II" : i === 2 ? "III" : (i + 1)}.
            </span>
            {r.name}
          </span>
          <span className="font-mono text-[#8c8c8c]">{r.value}</span>
        </div>
      ))}
    </div>
  );
}
